import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import {
  validationApi,
  type DeadlineRuleIssue,
  type StateValidationReport,
} from '../lib/api';

export default function Validation() {
  const [tab, setTab] = useState(0);
  const [statesReport, setStatesReport] = useState<StateValidationReport | null>(null);
  const [rulesReport, setRulesReport] = useState<{
    totalChecked: number;
    withIssues: number;
    issues: DeadlineRuleIssue[];
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([validationApi.states(), validationApi.deadlineRules()])
      .then(([s, r]) => {
        setStatesReport(s);
        setRulesReport(r);
      })
      .catch(() => setError('Failed to load validation reports'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={1}>
        Data Integrity
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Cross-collection sanity checks for the compliance reference data.
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="States coverage" />
        <Tab label={`Deadline rules${rulesReport?.withIssues ? ` (${rulesReport.withIssues})` : ''}`} />
      </Tabs>

      {tab === 0 && statesReport && (
        <Stack spacing={3}>
          {Object.entries(statesReport).map(([dataset, info]) => (
            <Paper key={dataset} variant="outlined" sx={{ p: 2.5 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
                <Typography variant="subtitle1" fontWeight={700}>{dataset}</Typography>
                <Stack direction="row" spacing={1}>
                  <Chip size="small" label={`${info.count} rows`} />
                  {info.missing.length === 0 && info.unknown.length === 0 && info.duplicates.length === 0 ? (
                    <Chip size="small" color="success" label="OK" />
                  ) : (
                    <Chip size="small" color="warning" label="Issues" />
                  )}
                </Stack>
              </Stack>

              {info.missing.length > 0 && (
                <Box mb={1}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Missing states ({info.missing.length})
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap mt={0.5}>
                    {info.missing.map((m) => (
                      <Chip key={m} size="small" variant="outlined" label={m} />
                    ))}
                  </Stack>
                </Box>
              )}

              {info.unknown.length > 0 && (
                <Box mb={1}>
                  <Typography variant="caption" color="error" fontWeight={600}>
                    Unknown abbreviations ({info.unknown.length})
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap mt={0.5}>
                    {info.unknown.map((m) => (
                      <Chip key={m} size="small" color="error" variant="outlined" label={m} />
                    ))}
                  </Stack>
                </Box>
              )}

              {info.duplicates.length > 0 && (
                <Box>
                  <Typography variant="caption" color="warning.main" fontWeight={600}>
                    Duplicate rows ({info.duplicates.length})
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap mt={0.5}>
                    {info.duplicates.map((d) => (
                      <Chip
                        key={d.abbr}
                        size="small"
                        color="warning"
                        variant="outlined"
                        label={`${d.abbr} ×${d.count}`}
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              {info.missing.length === 0 && info.unknown.length === 0 && info.duplicates.length === 0 && (
                <Typography variant="body2" color="success.main">
                  ✓ Every state has exactly one row and no unknown abbreviations.
                </Typography>
              )}
            </Paper>
          ))}
        </Stack>
      )}

      {tab === 1 && rulesReport && (
        <>
          <Stack direction="row" spacing={1} mb={2}>
            <Chip label={`${rulesReport.totalChecked} states checked`} variant="outlined" />
            <Chip
              label={`${rulesReport.withIssues} with issues`}
              color={rulesReport.withIssues > 0 ? 'warning' : 'success'}
            />
          </Stack>

          <Paper variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>State</TableCell>
                  <TableCell>Issues</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rulesReport.issues.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2} align="center">
                      No issues found
                    </TableCell>
                  </TableRow>
                ) : (
                  rulesReport.issues.map((row) => (
                    <TableRow key={row.abbreviation}>
                      <TableCell>{row.state || row.abbreviation}</TableCell>
                      <TableCell>
                        <Stack spacing={0.25}>
                          {row.issues.map((issue, i) => (
                            <Typography key={i} variant="body2" color="text.secondary">
                              • {issue}
                            </Typography>
                          ))}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}
    </Box>
  );
}
